import React from 'react';
import { isNil } from 'lodash';
import type { Moment } from 'moment';
import moment from 'moment';

import Button from '~/components/core/Atomic/Buttons/Button';
import MenuItem from '~/components/core/Atomic/MenuItem';
import Tooltip from '~/components/core/Atomic/Tooltip';
import AutoComplete from '~/components/core/Molecules/Fields/AutoComplete';
import DatePickerTextFieldFormik from '~/components/core/Molecules/Fields/DatePickerField';
import { MonetaryValueTextField } from '~/components/core/Molecules/Fields/MonetaryValueTextField';
import TextField from '~/components/core/Molecules/Fields/TextField';
import StatusChip from '~/components/core/StatusChip';
import useCurrencyFormatter, { CurrencyFormatterContextProvider } from '~/components/CurrencyFormatterContext';
import DocumentLink from '~/components/Documents/DocumentLink';
import type { DamageAssessmentConfig } from '~/components/hooks/useDamageAssessmentConfig';
import TrashIcon from '~/components/icons/TrashIcon';
import UnlockedIcon from '~/components/icons/UnlockedIcon';
import InlineIconButton from '~/components/InlineIconButton';
import OverflowTextWithToolTip from '~/components/OverflowTextWithToolTip';
import type { ClaimPolymorph } from '~/components/types/claim-types';
import { COVERAGE_DECISIONS } from '~/components/types/invoice-types';
import Checkbox from '~/components/ui/atoms/Checkbox';
import FINANCIAL_STATUS_DICT from '~/server_shared/generated-types/FINANCIAL_STATUS_DICT';
import SUPPORTED_CURRENCIES from '~/server_shared/generated-types/SUPPORTED_CURRENCIES';
import { UpdateExposureLineItemsRequestCoverageDecisionOption as CoverageDecision } from '~/server_shared/openapi/data-contracts';
import { reportErrorInProductionOrThrow, stringCmp } from '~/Utils';
import { formatExchangeRate } from '~/Utils/currencyUtils';

import type { LineItemRow, ValueDescription } from './types';
import { DEPRECIATION_CATEGORIES } from './types';
import {
  buildEntityDepreciationMap,
  calcCoveredAmount,
  computeAutoFilledCoveredAmount,
  isDepreciationApplicable,
  isDepreciationEligible,
} from './utils';

const TOOLTIP_TEXT_SIZE = '200px';

const AmountSpecialCell: React.FC<{ amount: number }> = ({ amount }) => {
  const { currencyFormatter } = useCurrencyFormatter();
  return <>{currencyFormatter.format(amount || 0)}</>;
};

const withCurrencyContext = (content: React.ReactNode, currency?: string) => {
  if (currency) {
    return <CurrencyFormatterContextProvider currency={currency}>{content}</CurrencyFormatterContextProvider>;
  }
  return content;
};

export interface LineItemColumnParams {
  entities: ValueDescription[];
  disabled: boolean;
  anyLineItemHasExplanation: boolean;
  updateRow: (row: LineItemRow, field: string, value: string | number | boolean | undefined) => LineItemRow | undefined;
  setCurrentLineItems: React.Dispatch<React.SetStateAction<LineItemRow[]>>;
  mixpanelFocus: (field: string) => void;
  mixpanelBlur: (field: string) => void;
  classes: Record<string, string>;
  claim?: ClaimPolymorph;
  onCurrencyConversionClick?: (row: LineItemRow) => void;
  onDepreciationClick?: (row: LineItemRow) => Promise<LineItemRow | undefined>;
  policyCurrency?: string;
  convertingLineItemId?: string | null;
  isCalculatingDepreciation?: boolean;
  canOverrideDepreciation?: boolean;

  // Payment selection parameters
  isPaymentSelectionMode?: boolean;
  selectedPayableType?: 'expenses' | 'indemnity' | null;
  selectedLineItems?: Set<string>;
  onSelectionChange?: (itemId: string, selected: boolean) => void;

  // Config-driven column label overrides: columnId -> label string
  columnLabelOverrides?: Partial<Record<string, string>>;

  // Invoice-summary granularity: trash is hidden for already-saved rows so the
  // user can't orphan an invoice; new (unsaved) rows still get trash.
  isInvoiceGranularity?: boolean;

  /**
   * Per-row payable_type behavior driven by each row's exposure DA config.
   * Strictly opt-in: when `payableConfigGuardActive` is false (default, today's behavior),
   * the payable_type column short-circuits to the legacy 2-option dropdown for every row.
   * When true, each row's cell adapts based on its exposure's allowed_payable_types.
   */
  exposureToDAConfig?: Map<number, DamageAssessmentConfig>;
  payableConfigGuardActive?: boolean;
}

export type LineItemColumnId = keyof ReturnType<typeof getAllLineItemColumns>;

export function getAllLineItemColumns(params: LineItemColumnParams) {
  const isRowDisabled = (row: LineItemRow) => params.disabled || row.is_locked === true;
  const {
    entities,
    disabled,
    anyLineItemHasExplanation,
    updateRow,
    setCurrentLineItems,
    mixpanelFocus,
    mixpanelBlur,
    classes,
    claim,
    onCurrencyConversionClick,
    onDepreciationClick,
    policyCurrency,
    convertingLineItemId,
    isCalculatingDepreciation,
    canOverrideDepreciation = false,
    columnLabelOverrides = {},
    isInvoiceGranularity = false,
    exposureToDAConfig,
    payableConfigGuardActive = false,
  } = params;

  const getLabel = (columnId: string, defaultLabel: string): string => columnLabelOverrides[columnId] ?? defaultLabel;

  const sortedEntities = [...entities].sort((a, b) => stringCmp(a.description, b.description));

  const entityKeys = entities.map(({ value }) => value);
  const entityMap = entities.reduce(
    (acc, { value, description }) => {
      acc[value] = description;
      return acc;
    },
    {} as Record<string, string>
  );
  const entityDepreciationMap = buildEntityDepreciationMap(entities);

  return {
    order: { id: 'order', label: 'Order', isHidden: true },
    selection_checkbox: {
      id: 'selection_checkbox',
      disableSort: true,
      label: '',
      align: 'center' as const,
      specialCell: (row: LineItemRow) => {
        // Get selection state from params (will be passed from parent component)
        const isPaymentSelectionMode = params.isPaymentSelectionMode || false;
        const selectedPayableType = params.selectedPayableType;
        const selectedLineItems = params.selectedLineItems || new Set();
        const onSelectionChange = params.onSelectionChange;

        if (!isPaymentSelectionMode) {
          return null;
        }

        // Determine if this item can be selected
        // Check payable type, covered amount, active payment status, and locked status
        const hasActivePayment =
          row.payment_financial_status &&
          (() => {
            const statusInfo =
              FINANCIAL_STATUS_DICT[row.payment_financial_status as keyof typeof FINANCIAL_STATUS_DICT];
            return statusInfo && statusInfo.is_active;
          })();

        const canBeSelected =
          row.payable_type === selectedPayableType &&
          // Allow refund rows (negative covered_amount) to be chosen; only block true zero/null rows.
          (row.covered_amount || 0) !== 0 &&
          !hasActivePayment &&
          !row.is_locked;

        const isSelected = selectedLineItems.has(row.id);

        const getDisabledReason = () => {
          if (row.is_locked) {
            return 'Locked items cannot be selected';
          }
          if (row.payable_type !== selectedPayableType) {
            return `Only ${selectedPayableType} items can be selected`;
          }
          if ((row.covered_amount || 0) === 0) {
            return 'Covered amount cannot be 0';
          }
          if (hasActivePayment) {
            return 'Items with active payments cannot be selected';
          }
          return '';
        };

        const checkbox = (
          <Checkbox
            checked={isSelected}
            disabled={!canBeSelected}
            onChange={(e) => {
              if (onSelectionChange) {
                onSelectionChange(row.id, e.target.checked);
              }
            }}
            className={!canBeSelected ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
          />
        );

        return !canBeSelected ? (
          <Tooltip title={getDisabledReason()}>
            <span>{checkbox}</span>
          </Tooltip>
        ) : (
          checkbox
        );
      },
    },
    description: {
      id: 'description',
      disableSort: true,
      label: getLabel('description', 'Description'),
      specialCell: (row: LineItemRow) =>
        isRowDisabled(row) ? (
          <OverflowTextWithToolTip maxWidth={TOOLTIP_TEXT_SIZE}>{row.description ?? ''}</OverflowTextWithToolTip>
        ) : (
          <TextField
            inlineEditableField
            onChange={(value) => {
              updateRow(row, 'description', value);
            }}
            onFocus={() => mixpanelFocus('description')}
            onBlur={() => mixpanelBlur('description')}
            value={row.description}
            className={classes.textFieldRow}
          />
        ),
    },
    amount: {
      id: 'amount',
      disableSort: true,
      label: getLabel('amount', 'Amount'),
      specialCell: (row: LineItemRow) =>
        withCurrencyContext(
          <MonetaryValueTextField
            value={row.amount}
            onChange={(value) => {
              if (row.coverage_decision === 'covered') {
                const updatedRow = { ...row, amount: value };
                const newCoveredAmount = computeAutoFilledCoveredAmount(updatedRow);
                setCurrentLineItems((prev) =>
                  prev.map((item) =>
                    item.id !== row.id ? item : { ...item, amount: value, covered_amount: newCoveredAmount }
                  )
                );
              } else {
                updateRow(row, 'amount', value);
              }
            }}
            inlineEditableField
            disabled={isRowDisabled(row)}
            className="w-[90px]"
            onFocus={() => mixpanelFocus('amount')}
            onBlur={() => mixpanelBlur('amount')}
          />,
          row.amount_currency
        ),
    },
    amount_currency: {
      id: 'amount_currency',
      disableSort: true,
      label: getLabel('amount_currency', 'Currency'),
      specialCell: (row: LineItemRow) => {
        const currentCurrency = row.amount_currency || '';

        if (isRowDisabled(row)) {
          return <OverflowTextWithToolTip maxWidth={TOOLTIP_TEXT_SIZE}>{currentCurrency}</OverflowTextWithToolTip>;
        }

        return (
          <div className="full-width flex w-[80px]">
            <AutoComplete
              id={`currency-${row.id}`}
              options={SUPPORTED_CURRENCIES.map((c) => ({ value: c, label: c }))}
              value={currentCurrency ? { value: currentCurrency, label: currentCurrency } : null}
              getOptionLabel={(option) => option?.label || ''}
              onChange={(_, newValue) => {
                setCurrentLineItems((prevItems) =>
                  prevItems.map((item) => {
                    if (item.id !== row.id) {
                      return item;
                    }
                    return {
                      ...item,
                      amount_currency: newValue?.value || undefined,
                      // Reset conversion fields if currency changes
                      conversion_date: undefined,
                      conversion_rate: undefined,
                      base_amount: undefined,
                      covered_amount: 0,
                    };
                  })
                );
              }}
              textFieldProps={{
                label: currentCurrency ? undefined : 'Select',
                onFocus: () => mixpanelFocus('amount_currency'),
                onBlur: () => mixpanelBlur('amount_currency'),
              }}
            />
          </div>
        );
      },
    },
    item_date: {
      id: 'item_date',
      disableSort: true,
      label: getLabel('item_date', 'Date'),
      specialCell: (row: LineItemRow) => {
        const currentDate = row.item_date;

        return (
          <div className="full-width flex w-[140px]">
            <DatePickerTextFieldFormik
              id={`date-${row.id}`}
              value={currentDate ? moment(currentDate).format('YYYY-MM-DD') : null}
              disableFuture
              disabled={isRowDisabled(row)}
              fullWidth
              onChange={(date: Moment | null) => {
                const formattedDate = date?.format('YYYY-MM-DD') || undefined;

                setCurrentLineItems((prevItems) =>
                  prevItems.map((item) => {
                    if (item.id !== row.id) {
                      return item;
                    }
                    return {
                      ...item,
                      item_date: formattedDate,
                      // Changing the date invalidates existing conversions
                      conversion_date: undefined,
                      conversion_rate: undefined,
                      base_amount: undefined,
                      covered_amount: 0,
                    };
                  })
                );
              }}
            />
          </div>
        );
      },
    },
    coverage_decision: {
      id: 'coverage_decision',
      disableSort: true,
      label: getLabel('coverage_decision', 'Coverage Decision'),
      specialCell: (row: LineItemRow) => {
        const coverageDecision = row.coverage_decision;

        return (
          <div className="full-width flex w-[130px]">
            <TextField
              variant="outlined"
              inlineEditableField
              onChange={(value) => {
                const decision = value as CoverageDecision;
                const hasDepreciation = isDepreciationApplicable(row, entityDepreciationMap);

                let newCoveredAmount: number;
                if (decision === CoverageDecision.Covered && !hasDepreciation) {
                  newCoveredAmount = computeAutoFilledCoveredAmount({ ...row, coverage_decision: decision });
                } else if (decision !== CoverageDecision.Covered) {
                  newCoveredAmount = 0;
                } else {
                  // covered + depreciation: don't auto-fill, keep existing
                  setCurrentLineItems((prev) =>
                    prev.map((item) => (item.id !== row.id ? item : { ...item, coverage_decision: decision }))
                  );
                  return;
                }

                setCurrentLineItems((prev) =>
                  prev.map((item) =>
                    item.id !== row.id
                      ? item
                      : { ...item, coverage_decision: decision, covered_amount: newCoveredAmount }
                  )
                );
              }}
              onFocus={() => mixpanelFocus('coverage_decision')}
              onBlur={() => mixpanelBlur('coverage_decision')}
              label={coverageDecision ? undefined : 'Set Decision'}
              value={coverageDecision || ''}
              disabled={isRowDisabled(row)}
              select
            >
              {COVERAGE_DECISIONS.map(({ value, description }) => (
                <MenuItem key={value} value={value}>
                  {description}
                </MenuItem>
              ))}
            </TextField>
          </div>
        );
      },
    },
    third_party_amount: {
      id: 'third_party_amount',
      disableSort: true,
      label: getLabel('third_party_amount', 'Third Party Amount'),
      specialCell: (row: LineItemRow) =>
        withCurrencyContext(
          <MonetaryValueTextField
            value={row.third_party_amount}
            onChange={(value) => {
              // Update third_party_amount AND auto-compute covered_amount in a single state update
              const updatedRow = { ...row, third_party_amount: value };
              const newCoveredAmount = computeAutoFilledCoveredAmount(updatedRow);
              setCurrentLineItems((prevItems) =>
                prevItems.map((item) => {
                  if (item.id !== row.id) return item;
                  return { ...item, third_party_amount: value, covered_amount: newCoveredAmount };
                })
              );
            }}
            inlineEditableField
            disabled={isRowDisabled(row)}
            className="w-[90px]"
            onFocus={() => mixpanelFocus('third_party_amount')}
            onBlur={() => mixpanelBlur('third_party_amount')}
          />,
          row.amount_currency
        ),
    },
    payable_type: {
      id: 'payable_type',
      disableSort: true,
      label: getLabel('payable_type', 'Payable Type'),
      specialCell: (row: LineItemRow) => {
        const payableTypeOptions = [
          { value: 'expenses', label: 'Expenses' },
          { value: 'indemnity', label: 'Indemnity' },
        ];

        // When the per-row payable guard is active and the row's exposure has a single-value
        // allowed_payable_types (indemnity/expenses), render the configured value as a disabled
        // select (matching the legacy disabled look); if no exposure is picked yet, disable the
        // cell until one is chosen. Falls through to the legacy 2-option select for 'both',
        // null, and (most importantly) the entire flow when the guard is off.
        if (payableConfigGuardActive) {
          if (!row.exposure_id) {
            return (
              <div className="full-width flex w-[120px]">
                <TextField variant="outlined" inlineEditableField value="" label="Select exposure" disabled select>
                  {/* empty — disabled until exposure_id is picked */}
                </TextField>
              </div>
            );
          }
          const allowed = exposureToDAConfig?.get(row.exposure_id)?.allowed_payable_types;
          if (allowed === 'indemnity' || allowed === 'expenses') {
            return (
              <div className="full-width flex w-[120px]">
                <TextField variant="outlined" inlineEditableField value={allowed} disabled select>
                  {payableTypeOptions.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            );
          }
        }

        const payableType = row.payable_type;
        return (
          <div className="full-width flex w-[120px]">
            <TextField
              variant="outlined"
              inlineEditableField
              onChange={(value) => {
                updateRow(row, 'payable_type', value);
              }}
              onFocus={() => mixpanelFocus('payable_type')}
              onBlur={() => mixpanelBlur('payable_type')}
              label={payableType ? undefined : 'Select'}
              value={payableType || ''}
              disabled={isRowDisabled(row)}
              select
            >
              {payableTypeOptions.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </div>
        );
      },
    },
    coverage: {
      id: 'coverage',
      disableSort: true,
      label: 'Coverage',
      specialCell: (row: LineItemRow) => {
        const sanitizedCoverage = row.coverage && entityKeys.includes(row.coverage) ? row.coverage : '';

        if (isRowDisabled(row)) {
          return (
            <OverflowTextWithToolTip maxWidth={TOOLTIP_TEXT_SIZE}>
              {entityMap[sanitizedCoverage] || ''}
            </OverflowTextWithToolTip>
          );
        }

        return (
          <TextField
            variant="outlined"
            inlineEditableField
            onChange={(value) => {
              updateRow(row, 'coverage', value);
            }}
            className={classes.textFieldRow}
            select
            label={row.coverage ? undefined : 'Select coverage'}
            value={sanitizedCoverage}
            onFocus={() => mixpanelFocus('coverage')}
            onBlur={() => mixpanelBlur('coverage')}
            error={row.coverage_decision === 'covered' && !row.coverage}
          >
            <MenuItem value={undefined}>No Coverage</MenuItem>
            {sortedEntities.map(({ value, description }) => (
              <MenuItem key={value} value={value}>
                {description}
              </MenuItem>
            ))}
          </TextField>
        );
      },
    },
    exposure: {
      id: 'exposure',
      disableSort: true,
      label: 'Exposure',
      specialCell: (row: LineItemRow) => {
        const currentValue = row.exposure_id ? String(row.exposure_id) : '';

        return (
          <TextField
            variant="outlined"
            inlineEditableField
            disabled={isRowDisabled(row)}
            onChange={(value) => {
              const updatedRow = updateRow(row, 'exposure_id', value ? parseInt(value, 10) : undefined);
              // If the selected exposure has depreciation enabled, clear covered_amount
              if (value) {
                const hasDepreciation = entityDepreciationMap[value] === true;
                if (hasDepreciation && updatedRow) {
                  updateRow(updatedRow, 'covered_amount', 0);
                }
              }
            }}
            className={classes.textFieldRow}
            select
            label={currentValue ? undefined : 'Select exposure'}
            value={currentValue}
            onFocus={() => mixpanelFocus('exposure_id')}
            onBlur={() => mixpanelBlur('exposure_id')}
            error={row.coverage_decision === 'covered' && !row.exposure_id}
          >
            <MenuItem value={undefined}>No Exposure</MenuItem>
            {sortedEntities.map(({ value, description }) => (
              <MenuItem key={value} value={value}>
                {description}
              </MenuItem>
            ))}
          </TextField>
        );
      },
    },
    covered_amount: {
      id: 'covered_amount',
      disableSort: true,
      label: getLabel('covered_amount', 'Covered Amount'),
      specialCell: (row: LineItemRow) => {
        // Check if the selected exposure has depreciation enabled AND item is indemnity
        const hasDepreciation = isDepreciationApplicable(row, entityDepreciationMap);

        // Check if conversion is needed but not yet applied
        const hasMultiCurrency = Boolean(
          row.amount_currency && policyCurrency && row.amount_currency !== policyCurrency
        );
        const isMissingBaseAmount = hasMultiCurrency && !row.base_amount;

        return withCurrencyContext(
          <MonetaryValueTextField
            disabled={
              isRowDisabled(row) ||
              row.coverage_decision !== 'covered' ||
              isNil(row.amount) ||
              (hasDepreciation && !canOverrideDepreciation) ||
              isMissingBaseAmount
            }
            value={row.covered_amount}
            onChange={(value) => {
              const updatedRow = updateRow(row, 'covered_amount', calcCoveredAmount(row, value ?? 0));
              // If user has permission and depreciation is enabled, set override flag
              if (hasDepreciation && canOverrideDepreciation && updatedRow) {
                updateRow(updatedRow, 'is_depreciation_overridden', true);
              }
            }}
            fullWidth
            inlineEditableField
            className="w-[100px]"
            onFocus={() => mixpanelFocus('covered_amount')}
            onBlur={() => mixpanelBlur('covered_amount')}
          />,
          policyCurrency
        );
      },
    },
    currency_conversion: {
      id: 'currency_conversion',
      disableSort: true,
      label: 'Currency Conversion',
      specialCell: (row: LineItemRow) => {
        // Show button if row has different currency than policy or show dash
        const hasDifferentCurrency = row.amount_currency && policyCurrency && row.amount_currency !== policyCurrency;

        if (!hasDifferentCurrency) {
          return <>-</>;
        }

        const isConverting = convertingLineItemId === row.id;
        // Check if conversion has been applied (conversion always produces base_amount)
        const hasConversion = Boolean(row.conversion_date && row.conversion_rate && row.base_amount);
        const dateForConversion = row.item_date;
        const hasDate = Boolean(dateForConversion && dateForConversion.trim() !== '');
        const hasAmount = Boolean(row.amount && row.amount > 0);
        const isButtonDisabled = disabled || isConverting || !hasDate || !hasAmount;

        const button = (
          <Button
            size="small"
            variant="text"
            color="primary"
            onClick={() => onCurrencyConversionClick?.(row)}
            disabled={isButtonDisabled}
          >
            {isConverting ? 'APPLYING...' : hasConversion ? 'REAPPLY' : 'APPLY'}
          </Button>
        );

        // Show tooltip if disabled due to missing requirements
        if (!disabled && isButtonDisabled) {
          let tooltipMessage = '';
          if (!hasDate) {
            tooltipMessage = 'Date is required for currency conversion';
          } else if (!hasAmount) {
            tooltipMessage = 'Amount is required for currency conversion';
          }

          if (tooltipMessage) {
            return (
              <Tooltip title={tooltipMessage} placement="top">
                <span>{button}</span>
              </Tooltip>
            );
          }
        }

        return button;
      },
    },
    conversion_date: {
      id: 'conversion_date',
      disableSort: true,
      label: 'Date of Conversion',
      specialCell: (row: LineItemRow) => {
        return <>{row.conversion_date || '-'}</>;
      },
    },
    conversion_rate: {
      id: 'conversion_rate',
      disableSort: true,
      label: 'Exchange Rate',
      specialCell: (row: LineItemRow) => {
        return <>{row.conversion_rate ? formatExchangeRate(row.conversion_rate) : '-'}</>;
      },
    },
    base_amount: {
      id: 'base_amount',
      disableSort: true,
      label: `Amount${policyCurrency ? ` (${policyCurrency})` : ''}`,
      specialCell: (row: LineItemRow) => {
        // If currency matches policy currency, show amount, otherwise show base_amount
        const isSameCurrency = !row.amount_currency || row.amount_currency === policyCurrency;
        const amountToShow = isSameCurrency ? (row.amount ?? 0) : (row.base_amount ?? 0);
        return withCurrencyContext(<AmountSpecialCell amount={amountToShow} />, policyCurrency);
      },
    },
    explanation: {
      id: 'explanation',
      disableSort: true,
      label: getLabel('explanation', 'Explanation'),
      // Auto-hide when no items have explanation text, unless config explicitly controls this field
      isHidden: !anyLineItemHasExplanation && !columnLabelOverrides?.explanation,
      specialCell: (row: LineItemRow) =>
        isRowDisabled(row) ? (
          <OverflowTextWithToolTip maxWidth={TOOLTIP_TEXT_SIZE}>{row.explanation ?? ''}</OverflowTextWithToolTip>
        ) : (
          <TextField
            variant="outlined"
            placeholder="-"
            inlineEditableField
            onChange={(value) => {
              updateRow(row, 'explanation', value);
            }}
            className={classes.textFieldRow}
            value={row.explanation}
            onFocus={() => mixpanelFocus('explanation')}
            onBlur={() => mixpanelBlur('explanation')}
          />
        ),
    },
    document: {
      id: 'document',
      disableSort: true,
      label: getLabel('document', 'Document'),
      specialCell: (row: LineItemRow) => {
        if (!row.document_id || !claim) {
          return null;
        }
        const document = claim.documents?.find((d) => d.id === row.document_id);
        if (!document) {
          reportErrorInProductionOrThrow(
            `Document with id ${row.document_id} not found in claim documents for line item ${row.id}`
          );
          return null;
        }
        return (
          <DocumentLink document={document} text={document.document_name || `Doc #${document.claim_internal_id}`} />
        );
      },
    },
    category: {
      id: 'category',
      disableSort: true,
      label: 'Category',
      specialCell: (row: LineItemRow) => {
        const currentCategory = row.category || '';
        const isIndemnity = row.payable_type === 'indemnity';

        return (
          <TextField
            variant="outlined"
            inlineEditableField
            onChange={(value) => {
              updateRow(row, 'category', value);
            }}
            className={classes.textFieldRow}
            select
            label={currentCategory ? undefined : 'Select category'}
            value={currentCategory}
            onFocus={() => mixpanelFocus('category')}
            onBlur={() => mixpanelBlur('category')}
            disabled={isRowDisabled(row) || !isIndemnity}
          >
            <MenuItem value="">No Category</MenuItem>
            {DEPRECIATION_CATEGORIES.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        );
      },
    },
    depreciation: {
      id: 'depreciation',
      disableSort: true,
      label: 'Depreciation',
      specialCell: (row: LineItemRow) => {
        const isIndemnity = row.payable_type === 'indemnity';
        const { isEligible, reasons } = isDepreciationEligible(row, policyCurrency);

        // Add payable type check to reasons
        const allReasons = !isIndemnity ? ['Depreciation is only available for indemnity items'] : reasons;
        const isActuallyEligible = isIndemnity && isEligible;

        // Build tooltip message explaining why button is disabled
        const tooltipMessage = !disabled && !isActuallyEligible ? allReasons.join(', ') : '';

        const button = (
          <Button
            size="small"
            variant="text"
            color="primary"
            onClick={async () => {
              if (onDepreciationClick) {
                const updatedRow = await onDepreciationClick(row);
                if (updatedRow) {
                  setCurrentLineItems((prevItems) =>
                    prevItems.map((item) => (item.id === updatedRow.id ? updatedRow : item))
                  );
                } else {
                  // If no updated row returned after a successful click, this indicates a bug
                  reportErrorInProductionOrThrow(
                    `Depreciation calculation for line item ${row.id} did not return an updated row`
                  );
                }
              }
            }}
            disabled={disabled || !isActuallyEligible || isCalculatingDepreciation}
          >
            {isCalculatingDepreciation ? 'Calculating...' : 'Calculate'}
          </Button>
        );

        return tooltipMessage ? (
          <Tooltip title={tooltipMessage}>
            <span>{button}</span>
          </Tooltip>
        ) : (
          button
        );
      },
    },
    months_owned: {
      id: 'months_owned',
      disableSort: true,
      label: 'Months Owned',
      specialCell: (row: LineItemRow) => <>{isNil(row.months_owned) ? '-' : row.months_owned}</>,
    },
    financial_status: {
      id: 'financial_status',
      label: 'Payment status',
      disableSort: true,
      specialCell: (row: LineItemRow) => {
        if (!row.payment_financial_status) {
          return null;
        }
        return (
          <StatusChip
            status={StatusChip.STATUSES[row.payment_financial_status.toUpperCase() as keyof typeof StatusChip.STATUSES]}
            withIcon
          />
        );
      },
    },
    actions: {
      id: 'actions',
      disableSort: true,
      label: 'Actions',
      align: 'center' as const,
      specialCell: (row: LineItemRow) => {
        if (row.is_locked) {
          const lockReason = row.payment_request_id
            ? `This line item is locked because it's connected to a payment. Unlock to edit.`
            : 'This line item is locked. Unlock to edit';
          return (
            <InlineIconButton
              disabled={disabled}
              className={classes.hoverableNonFilledIcon}
              icon={UnlockedIcon}
              tooltipTitle={lockReason}
              onClick={() => {
                updateRow(row, 'is_locked', false);
              }}
            />
          );
        }
        // Invoice-summary mode: numeric id = already-saved row → no trash (would orphan
        // its invoice). UUIDs / placeholder ids belong to unsaved rows, where trash just
        // drops the row from local state before save.
        if (isInvoiceGranularity && /^\d+$/.test(row.id)) {
          return null;
        }
        return (
          <InlineIconButton
            disabled={disabled}
            className={classes.hoverableNonFilledIcon}
            icon={TrashIcon}
            tooltipTitle="Delete"
            onClick={() => setCurrentLineItems((currentRows) => currentRows.filter(({ id }) => id !== row.id))}
          />
        );
      },
    },
  };
}
